/**
 * "TAKING THE FIELD" — the branded curtain between the Versus splash and kickoff (`ui.matchIntro`, written by the
 * match scene). It carries the Versus palette (red HOME / blue AWAY halves on hype rays, both heads, the venue
 * chyron) while the stadium renderer mounts and, online, counts down to the shared kickoff time. When the first tick
 * has run (`phase: 'ready'`) the two halves slide apart to reveal the stadium and the HUD slides in underneath.
 */
import './intro.css';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { isCharacterId } from '@hf/shared';
import { Art, Glove, Head, Rays, Stroke } from '../components';
import { ui } from '@hf/client-core/ui-model/store';
import { introCopy, kickoffIn } from '@hf/client-core/ui-model/hud';
export { introCopy, kickoffIn } from '@hf/client-core/ui-model/hud';

/** Length of the curtain-lift animation (keep in sync with intro.css). */
export const INTRO_EXIT_MS = 650;

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function MatchIntro() {
  const intro = ui.matchIntro.value;
  const setup = ui.matchSetup.value;
  const [gone, setGone] = useState(false);
  const [now, setNow] = useState(nowMs);
  // Kickoff time in this clock, fixed when the countdown is published.
  const endsAt = useMemo(
    () => (intro?.phase === 'countdown' ? nowMs() + (intro.startsInMs ?? 0) : 0),
    [intro],
  );
  const phase = intro?.phase ?? null;

  useEffect(() => {
    if (phase !== 'ready') {
      setGone(false);
      return;
    }
    const t = setTimeout(() => setGone(true), INTRO_EXIT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (intro?.phase !== 'countdown') return;
    setNow(nowMs());
    const id = setInterval(() => setNow(nowMs()), 100);
    return () => clearInterval(id);
  }, [intro]);

  if (!intro || gone) return null;
  const [c0, c1] = setup?.config.characters ?? ['rookie', 'bigtex'];
  const ch0 = isCharacterId(c0) ? c0 : 'rookie';
  const ch1 = isCharacterId(c1) ? c1 : 'bigtex';
  const leaving = intro.phase === 'ready';
  const copy = introCopy(setup, ui.tutorialPlan.value?.lesson ?? null);
  const secs =
    intro.phase === 'countdown' ? kickoffIn(endsAt, Math.max(now, endsAt - (intro.startsInMs ?? 0))) : -1;
  return (
    <div
      class={`hf-intro ${leaving ? 'leaving' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={copy.title === 'PRACTICE FIELD' ? 'Practice field' : 'Taking the field'}
      data-testid="match-intro"
    >
      <div class="hf-intro__half home">
        <Rays a="#ff4a52" b="#8c1020" rays={20} x="0%" y="100%" speed={40} vignette={false} />
        <div class="hf-intro__head">
          <Head character={ch0} expression="determined" class="hf-intro__headart" />
          <Glove side="home" pose="ready" class="hf-intro__glove" />
        </div>
      </div>
      <div class="hf-intro__half away">
        <Rays a="#3d8bff" b="#0d2a70" rays={20} x="100%" y="0%" speed={40} reverse vignette={false} />
        <div class="hf-intro__head">
          <Head character={ch1} expression="determined" flip class="hf-intro__headart" />
          <Glove side="away" pose="ready" class="hf-intro__glove" />
        </div>
      </div>
      <div class="hf-intro__slash" aria-hidden="true" />
      <div class="hf-intro__center">
        <div class="hf-intro__kicker t-broadcast">{copy.kicker}</div>
        <Stroke text={copy.title} class="t-display hf-intro__title" />
        <div class="hf-intro__venue t-broadcast">{intro.venue}</div>
        <div class="hf-intro__status t-broadcast">
          {leaving ? (
            <span class="hf-intro__count">{copy.go}</span>
          ) : secs >= 0 ? (
            <span class="hf-intro__count" key={secs}>
              {copy.countdown ?? 'STARTING IN'} <b>{Math.max(1, secs)}</b>
            </span>
          ) : (
            <>
              <span class="hf-intro__ball" aria-hidden="true">
                <Art
                  keys={['prop/football']}
                  class="hf-intro__ballart"
                  fallback={<i class="hf-intro__ballfb" />}
                />
              </span>
              {intro.status ?? 'WARMING UP…'}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
